import * as THREE from 'three';

import { BuildingField } from '../game/buildings/BuildingField';
import { screenPlacer } from './ScreenPoint';

/**
 * How far along a building is, over the building.
 *
 * HTML above the canvas for the same reason the names are: the game renders
 * into a small buffer scaled up by a whole number, and a bar two pixels tall in
 * there is a bar four pixels tall with soft edges once it is blown up. This is
 * interface, not world.
 *
 * A bar appears when a building starts going up and disappears when it is
 * finished — nothing has to be told it is done, because the field stops
 * reporting it.
 */

export class BuildProgress {
  private readonly root = document.createElement('div');
  private readonly bars = new Map<string, { box: HTMLElement; fill: HTMLElement }>();

  constructor(
    private readonly container: HTMLElement,
    private readonly canvas: HTMLCanvasElement,
    private readonly field: BuildingField
  ) {
    this.root.className = 'build-progress';
    this.container.appendChild(this.root);
  }

  /**
   * `extra` to budowy spoza pola budynków — dziś mosty — z tym samym kształtem:
   * gdzie wisi pasek i ile już zrobiono.
   */
  update(
    camera: THREE.OrthographicCamera,
    extra: { id: string; position: THREE.Vector3; progress: number }[] = []
  ): void {
    const raising = [...this.field.raising(), ...extra];

    // Nothing under construction is the common case, and it costs a layout
    // measurement to find out where anything is — so it is not made.
    if (raising.length === 0) {
      if (this.bars.size) this.clear();
      return;
    }

    const place = screenPlacer(this.container, this.canvas, camera);
    const seen = new Set<string>();

    for (const { id, position, progress } of raising) {
      seen.add(id);

      let bar = this.bars.get(id);
      if (!bar) {
        const box = document.createElement('div');
        box.className = 'build-bar';
        const fill = document.createElement('div');
        fill.className = 'build-bar-fill';
        box.appendChild(fill);
        this.root.appendChild(box);
        bar = { box, fill };
        this.bars.set(id, bar);
      }

      const at = place(position);
      bar.box.style.display = at ? 'block' : 'none';
      if (!at) continue;

      bar.box.style.transform = `translate(${Math.round(at.x)}px, ${Math.round(at.y)}px) translate(-50%, -100%)`;
      bar.fill.style.width = `${Math.round(progress * 100)}%`;
    }

    for (const [id, bar] of this.bars) {
      if (seen.has(id)) continue;
      bar.box.remove();
      this.bars.delete(id);
    }
  }

  private clear(): void {
    for (const bar of this.bars.values()) bar.box.remove();
    this.bars.clear();
  }

  dispose(): void {
    this.clear();
    this.root.remove();
  }
}
